// Screen · one teacher, for the Lead — route /team/:id (2026-09-30 redesign).
//
// Everything the Lead needs about one colleague in one place: their classes and this week's status,
// their plans this term, observation records (and the buttons to start one), and the Lead's private
// notes. It works for EVERY active teacher — with or without a class or a plan (the old Insights
// drill could only be reached for teachers who already had plans). Lead-only (router role guard +
// every API it reads is Lead-gated). Never team-visible; no scores, no ranking.

import { api } from "../api";
import { esc } from "../dom";
import { icon } from "../icons";
import { navigate } from "../router";
import { byGrade, type ClassRow } from "../lesson-data";
import { pageHeader, statusChip, emptyState, toast } from "../ui";
import { classStateThisWeek } from "./home";
import type { AdminUser } from "./team";
import type { LeadTeachingDTO } from "../../functions/api/_lib/lesson-delivery";

type LessonStatus = "not_started" | "in_progress" | "submitted" | "approved";
interface TimelineRow {
  class_name: string;
  class_id: string;
  lesson_id: string;
  topic: string;
  week_no: number;
  status: LessonStatus;
  on_time: boolean | null;
  plan_id: string | null;
  teaching_state: "not_recorded" | "taught" | "confirmation_withdrawn";
}
interface Rollup { teacher_id: string; total: number; submitted: number; approved: number; drafts: number }
interface Tracking {
  last_review: string | null;
  last_observation: string | null;
  current_focus: string | null;
  next_action: string | null;
  updated_at: string | null;
}
interface ObsRow { id: string; kind: "classroom" | "marking"; observed_on: string; class_label: string | null; status: "draft" | "finalized" | "acknowledged" }
interface TeamWeekLesson { teacher_name: string; class_name: string; status: LessonStatus }

const OBS_KIND = { classroom: "Classroom observation", marking: "Book & worksheet check" } as const;
function obsChip(s: ObsRow["status"]): string {
  if (s === "draft") return `<span class="st st-draft chip">Draft — only you</span>`;
  if (s === "finalized") return `<span class="st st-submitted chip submitted">Waiting for their signature</span>`;
  return `<span class="st st-approved chip approved">${icon("check")}Signed</span>`;
}

export async function renderTeacher(page: HTMLElement, params: Record<string, string>, query: URLSearchParams): Promise<void> {
  const id = params.id ?? "";
  const q = encodeURIComponent(id);
  const selectedTerm = query.get("term_id") ?? "";
  let teachingFailure = "";
  const [{ users }, { classes }, ins, trk, obs, team, teaching] = await Promise.all([
    api<{ users: AdminUser[] }>("/api/admin/users"),
    api<{ classes: ClassRow[] }>("/api/classes"),
    selectedTerm ? Promise.resolve({ term: null, teachers: [] as Rollup[], timeline: [] as TimelineRow[] }) : api<{ term: { id: string; name: string } | null; teachers: Rollup[]; timeline?: TimelineRow[] }>(`/api/lead/insights?teacher=${q}`).catch(() => ({ term: null, teachers: [] as Rollup[], timeline: [] as TimelineRow[] })),
    api<{ tracking: Tracking | null }>(`/api/lead/tracking/${q}`).catch(() => ({ tracking: null })),
    api<{ observations: ObsRow[] }>(`/api/observations?teacher=${q}`).catch(() => ({ observations: [] as ObsRow[] })),
    api<{ lessons: TeamWeekLesson[] }>("/api/dashboard").catch(() => ({ lessons: [] as TeamWeekLesson[] })),
    selectedTerm ? api<LeadTeachingDTO>(`/api/lead/teaching?term_id=${encodeURIComponent(selectedTerm)}&teacher_id=${q}&limit=20`, { cache: "no-store" }).catch((error: unknown) => { teachingFailure = error instanceof Error ? error.message : "Teaching history is unavailable."; return null; }) : Promise.resolve(null),
  ]);
  const u = users.find((x) => x.id === id);
  if (!u) {
    page.innerHTML = `${pageHeader({ title: "Teacher not found", crumbs: [{ label: "Team", href: "/team" }, { label: "Not found" }] })}
      ${emptyState("This account does not exist.", `<a class="btn secondary" data-link href="/team">Back to Team</a>`)}`;
    return;
  }
  const theirs = classes.filter((c) => c.teacher_id === u.id).sort(byGrade);
  const roll = ins.teachers.find((r) => r.teacher_id === u.id);
  const timeline = ins.timeline ?? [];
  const termId = teaching?.term?.id || selectedTerm || ins.term?.id || "";
  const termSuffix = termId ? `?term_id=${encodeURIComponent(termId)}` : "";
  const teachingHref = `/lead/teaching${termId ? `?teacher_id=${q}&term_id=${encodeURIComponent(termId)}` : ""}`;
  const tr = trk.tracking;

  const classList = theirs.length
    ? `<div class="list">${theirs.map((c) => `<div class="row-link static">
        <span class="class-badge">${esc(c.name)}</span>
        <span class="rl-main">${esc(c.level ?? "")}</span>
        <span class="rl-meta">${statusChip(classStateThisWeek(c, team.lessons, classes), { lead: true })}</span>
        <a class="linkbtn" data-link href="/roadmap?class=${encodeURIComponent(c.id)}">Year plan</a>
        <a class="linkbtn" data-link href="/classes/${encodeURIComponent(c.id)}${termSuffix}">Class</a>
        <a class="linkbtn" data-link data-gradebook-link href="/classes/${encodeURIComponent(c.id)}/gradebook">Private marks</a>
      </div>`).join("")}</div>`
    : emptyState(`${esc(u.name)} has no class yet, so their Home is empty.`, `<a class="btn secondary" data-link href="/admin?tab=classes">${icon("setup")}<span>Assign a class</span></a>`);

  const weeks = [...new Set(timeline.map((l) => l.week_no))].sort((a, b) => b - a);
  const plans = selectedTerm
    ? teachingFailure ? `<p class="error" role="alert" data-teacher-history-error>${esc(teachingFailure)} No current facts could be read for this selected term.</p><a class="linkbtn" data-link href="${esc(teachingHref)}">Retry in private teaching history</a>`
      : teaching?.term ? `<p class="hint" data-teacher-term="${esc(teaching.term.id)}">${esc(teaching.term.name)} · ${esc(teaching.term.start_date)} to ${esc(teaching.term.end_date)} · server snapshot ${esc(teaching.generated_at)}</p>${teaching.lessons.length ? `<div class="list">${teaching.lessons.map((l) => `<a class="row-link" data-link href="/lesson/${encodeURIComponent(l.id)}${termSuffix}"><span class="class-badge">${esc(l.class_name)}</span><span class="rl-main">${esc(l.topic)}<span class="muted small"> · planned Week ${l.week_no}${l.node_id ? ` · ${esc(l.node_id)}` : " · freeform"}<br />${l.latest_report ? `Actually taught ${esc(l.latest_report.taught_on)} · ${l.latest_report.coverage === "partial" ? "partial session" : "reported session content completed"} · ${l.latest_report.materials.length} retained material(s)` : l.teaching.state === "taught" ? "Exact content not recorded" : "No teaching update recorded"}</span></span>${icon("chevron", "ico-sm")}</a>`).join("")}</div>` : `<p class="muted">No lessons in this selected term.</p>`}<p><a class="linkbtn" data-link data-lead-teaching-link href="${esc(teachingHref)}">${teaching.next_cursor ? "All lessons and next page" : "Exact content and retained used materials"}</a></p>`
      : `<p class="error">Select an existing term in <a data-link href="${esc(teachingHref)}">private teaching history</a>.</p>`
    : timeline.length
    ? weeks.map((w) => `<h3 class="sub-title">Week ${w}</h3><div class="list">${timeline.filter((l) => l.week_no === w).map((l) => `
        <a class="row-link" data-link href="${l.plan_id ? `/plan/${esc(l.plan_id)}` : `/lesson/${esc(l.lesson_id)}`}${termSuffix}">
          <span class="class-badge">${esc(l.class_name)}</span>
          <span class="rl-main">${esc(l.topic)}</span>
          <span class="rl-meta">${statusChip(l.status, { lead: true })}${l.on_time === false && l.status === "submitted" ? ` <span class="muted small">(after the deadline)</span>` : ""}${l.teaching_state === "taught" ? ` <span class="st st-taught chip taught">${icon("check")}Taught</span>` : ""}</span>
          ${icon("chevron", "ico-sm")}
        </a>`).join("")}</div>`).join("")
    : `<p class="muted">No plans yet this term.</p>`;

  const obsList = obs.observations.length
    ? `<div class="list">${obs.observations.map((o) => `<a class="row-link" data-link href="/observation/${esc(o.id)}">
        <span class="rl-main">${OBS_KIND[o.kind]}${o.class_label ? ` · ${esc(o.class_label)}` : ""}</span>
        <span class="rl-meta"><span class="muted small">${esc(o.observed_on)}</span> ${obsChip(o.status)}</span>
        ${icon("chevron", "ico-sm")}
      </a>`).join("")}</div>`
    : `<p class="muted">No observations yet.</p>`;

  const notes = `<form id="track" class="notes-form">
      <p class="muted small">Private to you — never shown to the team. Write what happened and a next step, not a verdict.${tr?.updated_at ? ` Last updated ${esc(tr.updated_at.slice(0, 10))}.` : ""}</p>
      <label>Last review<input id="t-review" name="last_review" type="date" value="${esc(tr?.last_review ?? "")}" /></label>
      <label>Last observation<input id="t-obs" name="last_observation" type="date" value="${esc(tr?.last_observation ?? "")}" /></label>
      <label>Current focus<textarea id="t-focus" name="current_focus" placeholder="What this teacher is working on">${esc(tr?.current_focus ?? "")}</textarea></label>
      <label>Next action<textarea id="t-next" name="next_action" placeholder="The next thing you'll do (observe, meet, follow up)">${esc(tr?.next_action ?? "")}</textarea></label>
      <div class="form-actions"><button class="btn" type="submit">Save notes</button></div>
    </form>`;

  const stats = selectedTerm ? teaching?.term ? `Selected term: ${teaching.term.name}` : "Selected term history unavailable" : roll
    ? `${roll.total} plan${roll.total === 1 ? "" : "s"} this term · ${roll.submitted} sent · ${roll.approved} approved`
    : "No plans yet this term";

  page.innerHTML = `
    ${pageHeader({
      crumbs: [{ label: "Team", href: "/team" }, { label: u.name }],
      eyebrow: u.auth_role === "lead" ? "Lead" : "Teacher",
      title: u.name,
      sub: `${esc(u.email)} · ${esc(stats)}`,
      actions: `<a class="btn secondary" data-link data-lead-teaching-link href="${esc(teachingHref)}">Teaching content and used materials</a><button class="btn secondary" type="button" data-act="new-obs" data-kind="classroom">${icon("obs")}<span>New observation</span></button>
        <button class="btn secondary" type="button" data-act="new-obs" data-kind="marking">${icon("sheet")}<span>New book check</span></button>`,
    })}
    <div class="lesson-grid">
      <div class="lesson-main">
        <h2 class="sec-title">Classes</h2>
        ${classList}
        <h2 class="sec-title">${selectedTerm ? "Plans and sessions in the selected term" : "Plans this term"}</h2>
        ${plans}
      </div>
      <aside class="lesson-aside">
        <section class="panel"><h2 class="panel-title">${icon("obs")}Observations</h2>${obsList}</section>
        <section class="panel"><h2 class="panel-title">${icon("pencil")}Your notes</h2>${notes}</section>
      </aside>
    </div>`;

  page.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-act="new-obs"]');
    if (!b) return;
    e.preventDefault();
    void (async () => {
      try {
        const created = await api<{ id: string }>("/api/observations", {
          method: "POST",
          body: JSON.stringify({ teacher_id: u.id, kind: b.dataset.kind === "marking" ? "marking" : "classroom" }),
        });
        navigate(`/observation/${created.id}`);
      } catch (ex) {
        toast((ex as Error).message || "Couldn't start the observation.");
      }
    })();
  });
  page.querySelector<HTMLFormElement>("#track")?.addEventListener("submit", (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const body = {
      last_review: String(fd.get("last_review") ?? ""),
      last_observation: String(fd.get("last_observation") ?? ""),
      current_focus: String(fd.get("current_focus") ?? ""),
      next_action: String(fd.get("next_action") ?? ""),
    };
    void (async () => {
      try {
        await api(`/api/lead/tracking/${q}`, { method: "PUT", body: JSON.stringify(body) });
        toast("Notes saved.");
      } catch (ex) {
        toast((ex as Error).message || "Couldn't save the notes.");
      }
    })();
  });
}
